import React, { createContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
// import { useAlerts } from '../ToastProvider';

import { useAuthUser } from 'components/shared/hooks/useAuthUser';
import { useManageTwilioAuthToken } from './hooks/private/useManageTwilioAuthToken';
import { useManageTwilioConnection } from './hooks/private/useManageTwilioConnection';
import { useManageTwilioConversations } from './hooks/private/useManageTwilioConversations';
import { useManageTwilioMessages } from './hooks/private/useManageTwilioMessages';

/**
 * @typedef TwilioContextValueProps
 *
 * @property {string} error
 * If an error occurs in the TwilioProvider, this will be a
 * user-friendly string
 *
 * @property {function} setImpersonatedUserId
 * Set the userId of the user to impersonate; if the authenticated
 * user is not an admin, this will have no effect
 *
 * @property {import('@twilio/conversations').Client} conversationsClient
 * The Twilio Conversations client
 *
 * @property {number} unreadConversationsCount
 * The number of conversations with unread messages
 *
 * @property {{ [conversationSid: string]: number }} unreadMessagesCountByConversationSID
 * A map of conversation SIDs to the number of unread messages in that
 * conversation
 *
 * @property {TwilioConnectionStatuses} connectionStatus
 * The current connection status of the Twilio Conversations client
 *
 * @property { (conversationSID: string, newState: boolean) => Promise<void> } handleUpdateConversationArchivedState
 * Archive a conversation at the speified conversationSid
 *
 * @property {{
 *  [conversationSID: string]:
 *    | import('@twilio/conversations/lib/message').Message[]
 *    | { loading: true }
 *  }} messagesByConversationSid
 * A map of conversation SIDs to the messages in that conversation
 *
 * @property { (conversationSID: string) => Promise<void> } getMessagesByConversationSid
 * Initiate a request to fetch the messages in a conversation. The
 * conversation's messages will be available in the messagesByConversationSid
 * property when loading finishes.
 *
 * @property { (
 *  conversationSID: string,
 *  messageContent: unknown
 * ) => Promise<void> } sendMessage
 * Send a message within the conversationSID-specified conversation with the
 * passed messageContent.
 */

/**
 * @description The TwilioProvider is a React context provider intended
 * to manage a singleton instance of the Twilio Conversations client.
 *
 * Dependency injection of an admin user viewing conversations on
 * behalf of an esthentician can be achieved with the `setImpersonatedUserId`
 * function.
 *
 * @type {React.Context<TwilioContextValueProps | undefined>}
 */
export const TwilioContext = createContext(undefined);

const TwilioProvider = ({ children }) => {
  const authUser = useAuthUser();

  /**
   * Mechanism to dependency-inject users that aren't the authenticated
   * user for admin impersonation.
   */
  const [impersonatedUserId, _setImpersonatedUserId] = useState(null);

  const { token, refreshToken, error } =
    useManageTwilioAuthToken(impersonatedUserId);

  const {
    handleUpdateConversationArchivedState,
    conversations,
    unreadConversationsCount,
    unreadMessagesCountByConversationSID,
    markConversationAsRead,
    ensureConversationParticipants,

    _Conversations,
    _handleConversationJoined,
    _handleConversationLeft,
    _handleConversationUpdated,
    _setConversationsByConversationSID,
  } = useManageTwilioConversations(impersonatedUserId);

  const {
    messagesByConversationSID,
    getMessagesForConversationSID,
    sendMessage,
    _bindMessageAddedEventListener,
  } = useManageTwilioMessages({
    conversationsByConversationSID: _Conversations,
    setConversationsByConversationSID: _setConversationsByConversationSID,
    identity: impersonatedUserId ?? authUser?.id,
    markConversationAsRead,
  });

  const { conversationsClient, connectionStatus } = useManageTwilioConnection({
    token,
    refreshToken,
    error,
  });

  /**
   * Refuse to dependency-inject an impersonated userId if the
   * authenticated user is not an admin; allow the state setter
   * to be called with null to clear the impersonated userId.
   */
  const setImpersonatedUserId = useCallback(
    userIdToImpersonate => {
      if (!userIdToImpersonate) return _setImpersonatedUserId(null);
      if (authUser.role === 'admin' && userIdToImpersonate)
        _setImpersonatedUserId(userIdToImpersonate);
    },
    [authUser]
  );

  return (
    <TwilioContext.Provider
      value={{
        handleUpdateConversationArchivedState,
        conversationsClient,
        unreadMessagesCountByConversationSID,
        unreadConversationsCount,
        error,
        identity: impersonatedUserId ?? authUser?.id,
        setImpersonatedUserId,
        connectionStatus,
        conversations,
        messagesByConversationSID,
        getMessagesForConversationSID,
        sendMessage,
        markConversationAsRead,
        bindMessageAddedEventListener: _bindMessageAddedEventListener,
        handleConversationJoined: _handleConversationJoined,
        handleConversationLeft: _handleConversationLeft,
        handleConversationUpdated: _handleConversationUpdated,
        ensureConversationParticipants,
      }}
    >
      {children}
    </TwilioContext.Provider>
  );
};

TwilioProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default TwilioProvider;
